// src/services/oauthService.ts
// OAuth service layer for handling social authentication

import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import {
  OAuthProviders,
  OAuthProviderType,
  OAUTH_CONFIG,
  getOAuthProviderConfig,
  isOAuthProviderAvailable,
} from '../config/oauth';
import {
  OAuthCredential,
  OAuthAuthResponse,
  OAuthError,
  GoogleCredentialResponse,
} from '../types/oauth';
import { User } from './authService';

/**
 * OAuth Service class
 * Handles authentication with various OAuth providers
 */
class OAuthService {
  /**
   * Store tokens in localStorage (consistent with authService)
   */
  private storeTokens(token: string): void {
    localStorage.setItem('accessToken', token);
  }

  /**
   * Create a standardized OAuth error
   */
  private createError(code: string, message: string, originalError?: unknown): OAuthError {
    return {
      code,
      message,
      originalError,
    };
  }

  /**
   * Validate that a provider is available before use
   */
  private validateProvider(provider: OAuthProviderType): void {
    if (!isOAuthProviderAvailable(provider)) {
      const config = getOAuthProviderConfig(provider);
      if (!config) {
        throw this.createError('PROVIDER_NOT_FOUND', OAUTH_CONFIG.ERRORS.PROVIDER_NOT_CONFIGURED);
      }
      if (!config.enabled) {
        throw this.createError('PROVIDER_DISABLED', OAUTH_CONFIG.ERRORS.PROVIDER_DISABLED);
      }
      if (!config.clientId) {
        throw this.createError('MISSING_CLIENT_ID', OAUTH_CONFIG.ERRORS.MISSING_CLIENT_ID);
      }
    }
  }

  /**
   * Authenticate with Google using credential response
   */
  public async authenticateWithGoogle(
    credentialResponse: GoogleCredentialResponse
  ): Promise<{ user: User; isNewUser?: boolean }> {
    this.validateProvider(OAuthProviders.GOOGLE);

    const credential: OAuthCredential = {
      provider: OAuthProviders.GOOGLE,
      credential: credentialResponse.credential,
    };

    return this.authenticateWithProvider(credential);
  }

  /**
   * Generic method to authenticate with any OAuth provider
   * Sends the credential to our backend for verification and user creation/login
   */
  public async authenticateWithProvider(
    credential: OAuthCredential
  ): Promise<{ user: User; isNewUser?: boolean }> {
    try {
      const endpoint = this.getEndpointForProvider(credential.provider);

      const response = await axios.post<OAuthAuthResponse>(endpoint, {
        credential: credential.credential,
        providerId: credential.providerId,
        nonce: credential.nonce,
      });

      const { token, user, isNewUser } = response.data;

      // Store the JWT token
      this.storeTokens(token);

      return { user, isNewUser };
    } catch (error: unknown) {
      // Handle axios errors
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.error ||
                       error.response?.data?.message ||
                       OAUTH_CONFIG.ERRORS.AUTHENTICATION_FAILED;
        throw this.createError('AUTH_FAILED', message, error);
      }

      // Handle other errors
      if (error instanceof Error) {
        throw this.createError('AUTH_FAILED', error.message, error);
      }

      throw this.createError('UNKNOWN_ERROR', OAUTH_CONFIG.ERRORS.AUTHENTICATION_FAILED, error);
    }
  }

  /**
   * Get the backend API endpoint for a specific provider
   */
  private getEndpointForProvider(provider: OAuthProviderType): string {
    switch (provider) {
      case OAuthProviders.GOOGLE:
        return API_ENDPOINTS.OAUTH_GOOGLE;
      case OAuthProviders.MICROSOFT:
        return API_ENDPOINTS.OAUTH_MICROSOFT;
      default:
        throw this.createError('INVALID_PROVIDER', `Unknown OAuth provider: ${provider}`);
    }
  }

  /**
   * Check if any OAuth providers are available
   */
  public hasAvailableProviders(): boolean {
    return (
      isOAuthProviderAvailable(OAuthProviders.GOOGLE) ||
      isOAuthProviderAvailable(OAuthProviders.MICROSOFT)
    );
  }

  /**
   * Get list of available provider IDs
   */
  public getAvailableProviders(): OAuthProviderType[] {
    const providers: OAuthProviderType[] = [];

    if (isOAuthProviderAvailable(OAuthProviders.GOOGLE)) {
      providers.push(OAuthProviders.GOOGLE);
    }
    if (isOAuthProviderAvailable(OAuthProviders.MICROSOFT)) {
      providers.push(OAuthProviders.MICROSOFT);
    }

    return providers;
  }
}

// Create and export singleton instance
const oauthService = new OAuthService();
export default oauthService;
